import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { integrationsApiService, type OAuthConnection } from '../services/integrationsApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

const PROVIDERS = [
  { key: 'google', label: 'Google Drive' },
  { key: 'salesforce', label: 'Salesforce' },
  { key: 'hubspot', label: 'HubSpot' },
]

const cardStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.lg,
  boxShadow: shadow.sm,
}

const btnStyle: React.CSSProperties = {
  padding: `${spacing.sm} ${spacing.lg}`,
  borderRadius: radius.sm,
  border: 'none',
  cursor: 'pointer',
  fontSize: fontSize.sm,
  fontWeight: 600,
}

export default function IntegrationsPage() {
  const [connections, setConnections] = useState<OAuthConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [connecting, setConnecting] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [searchParams] = useSearchParams()

  const connectedProvider = searchParams.get('connected')
  const errorParam = searchParams.get('error')

  useEffect(() => {
    load()
  }, [])

  async function load() {
    try {
      const list = await integrationsApiService.listConnections()
      setConnections(list)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load connections')
    } finally {
      setLoading(false)
    }
  }

  async function handleConnect(provider: string) {
    setConnecting(provider)
    try {
      const { authUrl } = await integrationsApiService.getAuthorizeUrl(provider)
      window.location.href = authUrl
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to initiate connection')
      setConnecting(null)
    }
  }

  async function handleDisconnect(id: string) {
    setDisconnecting(id)
    try {
      await integrationsApiService.deleteConnection(id)
      setConnections(prev => prev.filter(c => c.oAuthConnectionId !== id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to disconnect')
    } finally {
      setDisconnecting(null)
    }
  }

  const connectedProviders = new Set(connections.map(c => c.provider))

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: spacing.xl }}>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', marginBottom: spacing.lg, color: colors.text }}>
        Integrations
      </h1>

      {connectedProvider && (
        <div style={{ marginBottom: spacing.lg, padding: spacing.md, borderRadius: radius.sm, background: colors.successLight, color: colors.success, border: `1px solid ${colors.successBorder}`, fontSize: fontSize.sm }}>
          Successfully connected {PROVIDERS.find(p => p.key === connectedProvider)?.label ?? connectedProvider}.
        </div>
      )}

      {errorParam && (
        <div style={{ marginBottom: spacing.lg, padding: spacing.md, borderRadius: radius.sm, background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, fontSize: fontSize.sm }}>
          OAuth error: {errorParam}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: spacing.md, color: colors.error, fontSize: fontSize.sm }}>{error}</div>
      )}

      <div style={{ marginBottom: spacing.xl }}>
        <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md, color: colors.text }}>
          Available Providers
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {PROVIDERS.map(p => {
            const existing = connections.filter(c => c.provider === p.key)
            const isConnected = connectedProviders.has(p.key)
            return (
              <div key={p.key} style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>{p.label}</div>
                  {existing.length > 0 && (
                    <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
                      {existing.length} account{existing.length !== 1 ? 's' : ''} connected
                    </div>
                  )}
                </div>
                <button
                  style={{ ...btnStyle, background: colors.primary, color: colors.onPrimary, opacity: connecting === p.key ? 0.6 : 1 }}
                  disabled={connecting === p.key}
                  onClick={() => handleConnect(p.key)}
                >
                  {connecting === p.key ? 'Redirecting...' : isConnected ? 'Connect Another' : 'Connect'}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {!loading && connections.length > 0 && (
        <div>
          <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md, color: colors.text }}>
            Connected Accounts
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            {connections.map(c => (
              <div key={c.oAuthConnectionId} style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, color: colors.text }}>
                    {PROVIDERS.find(p => p.key === c.provider)?.label ?? c.provider}
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>{c.accountLabel}</div>
                  {c.scope && (
                    <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.xs }}>
                      Scopes: {c.scope}
                    </div>
                  )}
                  <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.xs }}>
                    Connected {new Date(c.createdAt).toLocaleDateString()}
                    {c.expiresAt && ` · Expires ${new Date(c.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>
                <button
                  style={{ ...btnStyle, background: colors.error, color: colors.onPrimary, opacity: disconnecting === c.oAuthConnectionId ? 0.6 : 1 }}
                  disabled={disconnecting === c.oAuthConnectionId}
                  onClick={() => handleDisconnect(c.oAuthConnectionId)}
                >
                  {disconnecting === c.oAuthConnectionId ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && connections.length === 0 && !connectedProvider && (
        <p style={{ color: colors.textMuted, fontSize: fontSize.sm }}>No accounts connected yet.</p>
      )}
    </div>
  )
}
